import type {
  BusinessType,
  CreatePermissionTemplateInput,
  PipelineKind,
  ProjectTemplatePhase,
  ProjectType,
  StageKind,
  TradeTemplate,
  WorkspaceSettings,
} from '@buildbee/contracts';
import { tradeTemplateSeed } from './trades';

export type StageTemplate = {
  name: string;
  kind: StageKind;
  probabilityBps: number;
  color: string;
  rottingDays: number | null;
};

export type PipelineTemplate = {
  name: string;
  kind: PipelineKind;
  isDefault: boolean;
  stages: StageTemplate[];
};

export type ProjectTemplateSeed = {
  name: string;
  description: string;
  projectType: ProjectType;
  phases: ProjectTemplatePhase[];
  costCodes: { code: string; name: string }[];
  documentFolders: (
    | 'contracts'
    | 'plans'
    | 'permits'
    | 'specs'
    | 'photos'
    | 'selections'
    | 'warranties'
    | 'closeout'
    | 'insurance'
    | 'general'
  )[];
  punchChecklist: string[];
};

export type WorkspaceBlueprint = {
  businessType: BusinessType;
  pipelines: PipelineTemplate[];
  projectTemplates: ProjectTemplateSeed[];
  permissionTemplates: CreatePermissionTemplateInput[];
  settings: Partial<WorkspaceSettings>;
};

const won = (name = 'Won'): StageTemplate => ({ name, kind: 'won', probabilityBps: 10_000, color: '#16a34a', rottingDays: null });
const lost = (name = 'Lost'): StageTemplate => ({ name, kind: 'lost', probabilityBps: 0, color: '#dc2626', rottingDays: null });
const open = (name: string, probabilityBps: number, color: string, rottingDays: number | null = 14): StageTemplate => ({
  name,
  kind: 'open',
  probabilityBps,
  color,
  rottingDays,
});

/** Standard stages from the plan: New -> Qualified -> Site visit/Estimating -> Proposal sent -> Won/Lost. */
export const STANDARD_STAGES: StageTemplate[] = [
  open('New', 1000, '#64748b', 3),
  open('Qualified', 2500, '#0ea5e9', 7),
  open('Site visit / Estimating', 4000, '#6366f1', 14),
  open('Proposal sent', 6000, '#f59e0b', 14),
  won(),
  lost(),
];

const RESIDENTIAL_PIPELINES: PipelineTemplate[] = [
  {
    name: 'Homeowner leads',
    kind: 'homeowner_leads',
    isDefault: true,
    stages: [
      open('New', 1000, '#64748b', 2),
      open('Qualified', 2500, '#0ea5e9', 7),
      open('Site visit / Estimating', 4000, '#6366f1', 14),
      open('Proposal sent', 6000, '#f59e0b', 14),
      open('Selections', 8000, '#a855f7', 21),
      won('Won'),
      lost('Lost'),
    ],
  },
];

const REMODELER_PIPELINES: PipelineTemplate[] = [
  {
    name: 'Remodel leads',
    kind: 'homeowner_leads',
    isDefault: true,
    stages: [
      open('New', 1000, '#64748b', 2),
      open('Qualified', 2500, '#0ea5e9', 7),
      open('Site visit / Estimating', 4000, '#6366f1', 10),
      open('Design / Selections', 5500, '#a855f7', 21),
      open('Proposal sent', 7000, '#f59e0b', 14),
      won(),
      lost(),
    ],
  },
  { name: 'Service & warranty', kind: 'general', isDefault: false, stages: STANDARD_STAGES },
];

const COMMERCIAL_PIPELINES: PipelineTemplate[] = [
  {
    name: 'Bid invites',
    kind: 'bid_invites',
    isDefault: true,
    stages: [
      open('New invite', 1000, '#64748b', 3),
      open('Go / no-go', 2000, '#0ea5e9', 5),
      open('Estimating', 3500, '#6366f1', 21),
      open('Bid submitted', 5000, '#f59e0b', 30),
      open('Negotiation', 7500, '#a855f7', 21),
      won('Awarded'),
      lost('Lost / declined'),
    ],
  },
  {
    name: 'RFP / negotiated work',
    kind: 'rfp',
    isDefault: false,
    stages: [
      open('New', 1000, '#64748b', 7),
      open('Qualified', 2500, '#0ea5e9', 14),
      open('RFP response', 4000, '#6366f1', 21),
      open('Proposal sent', 6000, '#f59e0b', 30),
      open('Interview / negotiation', 8000, '#a855f7', 21),
      won(),
      lost(),
    ],
  },
];

const SUB_PIPELINES: PipelineTemplate[] = [
  {
    name: 'Bids to GCs',
    kind: 'gc_bids',
    isDefault: true,
    stages: [
      open('Invited to bid', 1000, '#64748b', 3),
      open('Takeoff / Estimating', 3000, '#6366f1', 14),
      open('Bid sent', 5000, '#f59e0b', 30),
      open('Scope review', 7000, '#a855f7', 14),
      won('Awarded'),
      lost('Not awarded'),
    ],
  },
  { name: 'Direct customers', kind: 'general', isDefault: false, stages: STANDARD_STAGES },
];

const COMMON_FOLDERS: ProjectTemplateSeed['documentFolders'] = [
  'contracts',
  'plans',
  'permits',
  'specs',
  'photos',
  'warranties',
  'closeout',
  'general',
];

const NEW_HOME: ProjectTemplateSeed = {
  name: 'New home build',
  description: 'Single-family new construction, from permit to closeout.',
  projectType: 'new_home',
  phases: [
    { name: 'Preconstruction & permits', durationDays: 30, offsetDays: 0, tasks: [{ name: 'Permit submission', durationDays: 21 }, { name: 'Selections kickoff', durationDays: 5 }] },
    { name: 'Site work & foundation', durationDays: 21, offsetDays: 30, tasks: [{ name: 'Excavation', durationDays: 5 }, { name: 'Footings & foundation', durationDays: 10 }, { name: 'Foundation inspection', durationDays: 1 }] },
    { name: 'Framing', durationDays: 25, offsetDays: 51, tasks: [{ name: 'Floor & wall framing', durationDays: 15 }, { name: 'Roof framing & sheathing', durationDays: 8 }, { name: 'Framing inspection', durationDays: 1 }] },
    { name: 'Rough-ins (MEP)', durationDays: 20, offsetDays: 76, tasks: [{ name: 'Plumbing rough', durationDays: 6 }, { name: 'Electrical rough', durationDays: 6 }, { name: 'HVAC rough', durationDays: 6 }, { name: 'Rough inspections', durationDays: 2 }] },
    { name: 'Insulation & drywall', durationDays: 18, offsetDays: 96, tasks: [] },
    { name: 'Finishes', durationDays: 40, offsetDays: 114, tasks: [{ name: 'Cabinets & counters', durationDays: 10 }, { name: 'Flooring', durationDays: 8 }, { name: 'Trim & paint', durationDays: 14 }] },
    { name: 'Punch & closeout', durationDays: 14, offsetDays: 154, tasks: [{ name: 'Final inspection / CO', durationDays: 2 }, { name: 'Homeowner walkthrough', durationDays: 1 }] },
  ],
  costCodes: [
    { code: '01-000', name: 'General conditions' },
    { code: '02-000', name: 'Site work' },
    { code: '03-000', name: 'Concrete' },
    { code: '06-000', name: 'Framing & carpentry' },
    { code: '07-000', name: 'Roofing & insulation' },
    { code: '08-000', name: 'Doors & windows' },
    { code: '09-000', name: 'Finishes' },
    { code: '22-000', name: 'Plumbing' },
    { code: '23-000', name: 'HVAC' },
    { code: '26-000', name: 'Electrical' },
  ],
  documentFolders: [...COMMON_FOLDERS, 'selections'],
  punchChecklist: ['Paint touch-up', 'Doors adjust & latch', 'Caulk wet areas', 'Clean windows', 'Test all GFCIs'],
};

const KITCHEN_REMODEL: ProjectTemplateSeed = {
  name: 'Kitchen remodel',
  description: 'Demo through final finishes for a kitchen remodel.',
  projectType: 'remodel',
  phases: [
    { name: 'Design & selections', durationDays: 21, offsetDays: 0, tasks: [{ name: 'Cabinet order', durationDays: 1 }] },
    { name: 'Demo & protection', durationDays: 4, offsetDays: 21, tasks: [] },
    { name: 'Rough-ins', durationDays: 7, offsetDays: 25, tasks: [{ name: 'Rough inspection', durationDays: 1 }] },
    { name: 'Drywall & paint', durationDays: 7, offsetDays: 32, tasks: [] },
    { name: 'Cabinets, counters, backsplash', durationDays: 14, offsetDays: 39, tasks: [{ name: 'Countertop template', durationDays: 1 }, { name: 'Countertop install', durationDays: 1 }] },
    { name: 'Punch & closeout', durationDays: 5, offsetDays: 53, tasks: [] },
  ],
  costCodes: [
    { code: '01-000', name: 'General conditions' },
    { code: '02-100', name: 'Demolition' },
    { code: '06-400', name: 'Cabinetry' },
    { code: '09-000', name: 'Finishes' },
    { code: '22-000', name: 'Plumbing' },
    { code: '26-000', name: 'Electrical' },
  ],
  documentFolders: [...COMMON_FOLDERS, 'selections'],
  punchChecklist: ['Cabinet door alignment', 'Appliance operation check', 'Grout/caulk', 'Final clean'],
};

const BATH_REMODEL: ProjectTemplateSeed = {
  ...KITCHEN_REMODEL,
  name: 'Bathroom remodel',
  description: 'Bathroom gut-and-replace remodel.',
  phases: [
    { name: 'Selections', durationDays: 14, offsetDays: 0, tasks: [] },
    { name: 'Demo', durationDays: 3, offsetDays: 14, tasks: [] },
    { name: 'Rough plumbing & electrical', durationDays: 5, offsetDays: 17, tasks: [{ name: 'Rough inspection', durationDays: 1 }] },
    { name: 'Waterproofing & tile', durationDays: 10, offsetDays: 22, tasks: [] },
    { name: 'Fixtures & finish', durationDays: 6, offsetDays: 32, tasks: [] },
  ],
};

const COMMERCIAL_TI: ProjectTemplateSeed = {
  name: 'Tenant improvement',
  description: 'Commercial interior fit-out with submittals, RFIs and owner billing.',
  projectType: 'tenant_improvement',
  phases: [
    { name: 'Preconstruction & submittals', durationDays: 30, offsetDays: 0, tasks: [{ name: 'Submittal log issued', durationDays: 5 }, { name: 'Long-lead procurement', durationDays: 20 }] },
    { name: 'Demo & layout', durationDays: 10, offsetDays: 30, tasks: [] },
    { name: 'MEP rough & framing', durationDays: 30, offsetDays: 40, tasks: [{ name: 'Above-ceiling inspection', durationDays: 2 }] },
    { name: 'Finishes', durationDays: 30, offsetDays: 70, tasks: [] },
    { name: 'Commissioning & closeout', durationDays: 14, offsetDays: 100, tasks: [{ name: 'O&M manuals', durationDays: 5 }, { name: 'Certificate of occupancy', durationDays: 3 }] },
  ],
  costCodes: [
    { code: '01-000', name: 'General requirements' },
    { code: '02-000', name: 'Existing conditions' },
    { code: '05-000', name: 'Metals' },
    { code: '09-000', name: 'Finishes' },
    { code: '10-000', name: 'Specialties' },
    { code: '21-000', name: 'Fire suppression' },
    { code: '22-000', name: 'Plumbing' },
    { code: '23-000', name: 'HVAC' },
    { code: '26-000', name: 'Electrical' },
    { code: '27-000', name: 'Communications' },
  ],
  documentFolders: [...COMMON_FOLDERS, 'insurance'],
  punchChecklist: ['Ceiling tiles', 'Life safety devices tested', 'Signage', 'Final clean'],
};

const COMMERCIAL_GROUND_UP: ProjectTemplateSeed = {
  ...COMMERCIAL_TI,
  name: 'Commercial ground-up',
  description: 'New commercial building with full submittal/RFI workflow.',
  projectType: 'commercial_new',
  phases: [
    { name: 'Preconstruction', durationDays: 60, offsetDays: 0, tasks: [{ name: 'Buyout', durationDays: 30 }] },
    { name: 'Sitework & foundations', durationDays: 60, offsetDays: 60, tasks: [] },
    { name: 'Structure', durationDays: 90, offsetDays: 120, tasks: [] },
    { name: 'Envelope', durationDays: 60, offsetDays: 210, tasks: [] },
    { name: 'Interiors & MEP', durationDays: 120, offsetDays: 240, tasks: [] },
    { name: 'Closeout', durationDays: 30, offsetDays: 360, tasks: [] },
  ],
};

const TRADE_PACKAGE: ProjectTemplateSeed = {
  name: 'Trade package',
  description: 'Subcontract scope on a GC-run project: mobilize, install, inspect, closeout.',
  projectType: 'trade_package',
  phases: [
    { name: 'Submittals & procurement', durationDays: 21, offsetDays: 0, tasks: [] },
    { name: 'Mobilization', durationDays: 3, offsetDays: 21, tasks: [] },
    { name: 'Rough-in', durationDays: 20, offsetDays: 24, tasks: [{ name: 'Rough inspection', durationDays: 1 }] },
    { name: 'Trim / finish', durationDays: 10, offsetDays: 44, tasks: [{ name: 'Final inspection', durationDays: 1 }] },
    { name: 'Closeout & lien waivers', durationDays: 7, offsetDays: 54, tasks: [] },
  ],
  costCodes: [
    { code: '100', name: 'Labor' },
    { code: '200', name: 'Material' },
    { code: '300', name: 'Equipment' },
    { code: '400', name: 'Permits & fees' },
  ],
  documentFolders: ['contracts', 'plans', 'specs', 'photos', 'insurance', 'closeout', 'general'],
  punchChecklist: ['Labeling complete', 'Test reports submitted', 'Area cleaned'],
};

const SERVICE_CALL: ProjectTemplateSeed = {
  name: 'Service call',
  description: 'Small service or warranty job.',
  projectType: 'service',
  phases: [{ name: 'Service visit', durationDays: 1, offsetDays: 0, tasks: [] }],
  costCodes: [
    { code: '100', name: 'Labor' },
    { code: '200', name: 'Material' },
  ],
  documentFolders: ['photos', 'general'],
  punchChecklist: [],
};

/** System permission templates every workspace starts with (editable copies). */
export const DEFAULT_PERMISSION_TEMPLATES: CreatePermissionTemplateInput[] = [
  {
    name: 'Office admin (no billing)',
    description: 'Full access except subscription billing and payments.',
    baseRole: 'admin',
    permissions: { allow: [], deny: ['billing:*', 'payment:manage'] },
  },
  {
    name: 'Estimator (no financials)',
    description: 'Sales/estimating without invoice or payment visibility.',
    baseRole: 'sales',
    permissions: { allow: [], deny: ['invoice:*', 'payment:*', 'budget:*'] },
  },
  {
    name: 'Project manager with approvals',
    description: 'PM who can approve change orders and send invoices.',
    baseRole: 'pm',
    permissions: { allow: ['change_order:approve', 'invoice:approve', 'payment:create'], deny: [] },
  },
  {
    name: 'Field foreman',
    description: 'Field user who can also manage schedule and RFIs on assigned projects.',
    baseRole: 'field',
    permissions: { allow: ['schedule:*', 'rfi:update', 'submittal:view'], deny: [] },
  },
  {
    name: 'Subcontractor: punch only',
    description: 'Sub who only sees and updates punch items assigned to them.',
    baseRole: 'sub',
    permissions: {
      allow: [],
      deny: ['submittal:*', 'bid:*', 'bid_package:*', 'rfi:create'],
      ownRecordsOnly: true,
    },
  },
];

const BLUEPRINTS: Record<BusinessType, WorkspaceBlueprint> = {
  residential: {
    businessType: 'residential',
    pipelines: RESIDENTIAL_PIPELINES,
    projectTemplates: [NEW_HOME, SERVICE_CALL],
    permissionTemplates: DEFAULT_PERMISSION_TEMPLATES,
    settings: { defaultMarkupBps: 1800, defaultRetainageBps: 0 },
  },
  remodeler: {
    businessType: 'remodeler',
    pipelines: REMODELER_PIPELINES,
    projectTemplates: [KITCHEN_REMODEL, BATH_REMODEL, SERVICE_CALL],
    permissionTemplates: DEFAULT_PERMISSION_TEMPLATES,
    settings: { defaultMarkupBps: 2500, defaultRetainageBps: 0 },
  },
  commercial: {
    businessType: 'commercial',
    pipelines: COMMERCIAL_PIPELINES,
    projectTemplates: [COMMERCIAL_TI, COMMERCIAL_GROUND_UP],
    permissionTemplates: DEFAULT_PERMISSION_TEMPLATES,
    settings: { defaultMarkupBps: 1000, defaultRetainageBps: 1000, paymentTermsDays: 30 },
  },
  specialty_sub: {
    businessType: 'specialty_sub',
    pipelines: SUB_PIPELINES,
    projectTemplates: [TRADE_PACKAGE, SERVICE_CALL],
    permissionTemplates: DEFAULT_PERMISSION_TEMPLATES,
    settings: { defaultMarkupBps: 1500, defaultRetainageBps: 1000, paymentTermsDays: 45 },
  },
};

/**
 * Everything a new workspace of the given business type is provisioned with. A trade template puts
 * its own pipelines (its default first) and project templates ahead of the business type's.
 */
export function workspaceBlueprint(businessType: BusinessType, trade?: TradeTemplate | null): WorkspaceBlueprint {
  const base = BLUEPRINTS[businessType];
  if (!trade) return base;
  const seed = tradeTemplateSeed(trade);
  const tradeHasDefault = seed.pipelines.some((p) => p.isDefault);
  const tradeTemplateNames = new Set(seed.projectTemplates.map((t) => t.name));
  return {
    ...base,
    pipelines: [...seed.pipelines, ...base.pipelines.map((p) => (tradeHasDefault ? { ...p, isDefault: false } : p))],
    projectTemplates: [...seed.projectTemplates, ...base.projectTemplates.filter((t) => !tradeTemplateNames.has(t.name))],
    settings: { ...base.settings, tradeTemplate: trade },
  };
}
